/* iter154 — Renders a business's category as "L1 · L2" (Level-1 followed
 * by an optional Level-2 sub-category), styled per spec: L1 normal
 * weight, L2 in muted smaller text. Falls back to L1 only when no
 * subcategory is present. Uses the read-through hierarchy cache so it
 * works offline.
 */
import { useMemo } from "react";
import { findCategory } from "../lib/categoryHierarchy";
import { categoryLabel as legacyLabel } from "../lib/geo";
import { useI18n } from "../lib/i18n";

/**
 * @param {object} props
 * @param {string} props.category   Level-1 slug
 * @param {string} [props.subcategory] Level-2 slug
 * @param {boolean} [props.inline]  When true, renders as a single line
 *                                  suitable for card meta-strips.
 * @param {string} [props.separator] Character between L1 and L2 in inline
 *                                   mode. Defaults to " · ".
 * @param {string} [props.className] Extra classes for the wrapper.
 */
export function CategoryLabel({ category, subcategory, inline = true, separator = " · ", className = "" }) {
  const { lang } = useI18n();

  const { l1, l2 } = useMemo(() => {
    const l1meta = findCategory(category);
    const l1label = l1meta ? (lang === "es" ? l1meta.name_es : l1meta.name_en)
                            : legacyLabel(category);
    if (!subcategory) return { l1: l1label, l2: null };
    const l2meta = findCategory(subcategory, { parentSlug: category });
    if (!l2meta) return { l1: l1label, l2: null };
    return {
      l1: l1label,
      l2: lang === "es" ? l2meta.name_es : l2meta.name_en,
    };
  }, [category, subcategory, lang]);

  if (inline) {
    return (
      <span className={className} data-testid={`cat-label-${category}${subcategory ? `-${subcategory}` : ""}`}>
        <span>{l1}</span>
        {l2 && (
          <>
            <span className="opacity-60">{separator}</span>
            <span className="opacity-75 text-[0.9em]">{l2}</span>
          </>
        )}
      </span>
    );
  }

  return (
    <span className={className} data-testid={`cat-label-${category}${subcategory ? `-${subcategory}` : ""}`}>
      <span className="block">{l1}</span>
      {l2 && <span className="block opacity-75 text-[0.85em]">{l2}</span>}
    </span>
  );
}

/**
 * Convenience: string-only version for when JSX isn't practical (e.g.
 * inside toString-joined meta strings). Renders "L1 · L2" or "L1".
 * NOTE: prefer <CategoryLabel/> when possible so the L2 gets muted
 * styling per design spec.
 */
export function categoryLabelText({ category, subcategory }, lang = "en") {
  const l1meta = findCategory(category);
  const l1 = l1meta ? (lang === "es" ? l1meta.name_es : l1meta.name_en) : legacyLabel(category);
  if (!subcategory) return l1;
  const l2meta = findCategory(subcategory, { parentSlug: category });
  if (!l2meta) return l1;
  const l2 = lang === "es" ? l2meta.name_es : l2meta.name_en;
  return `${l1} · ${l2}`;
}
